import { useRef, useState } from "react";
import { FileText, Trash2, Upload, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, authedUrl, fmtDate } from "@/lib/api";
import { DOC_KINDS } from "@/lib/constants";

export function DocumentsPanel({ booking, docs, canEdit, onChange }) {
  const [kind, setKind] = useState("e_ticket");
  const [busy, setBusy] = useState(false);
  const inp = useRef();

  async function upload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f); fd.append("kind", kind);
      try { await api.post(`/bookings/${booking.id}/documents`, fd); toast.success(`${f.name} uploaded`); }
      catch (ex) { toast.error(apiError(ex, `Upload failed: ${f.name}`)); }
    }
    setBusy(false); e.target.value = ""; onChange?.();
  }
  async function remove(d) {
    if (!window.confirm(`Delete ${d.original_filename}?`)) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Document removed"); onChange?.(); }
    catch (ex) { toast.error(apiError(ex)); }
  }

  return (
    <div className="panel p-5" data-testid="documents-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow">Documents & attachments</div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <select data-testid="doc-kind-select" className="field h-8 w-48 text-xs" value={kind} onChange={(e) => setKind(e.target.value)}>{Object.entries(DOC_KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
            <button data-testid="doc-upload-btn" disabled={busy} onClick={() => inp.current.click()} className="inline-flex items-center gap-1 h-8 px-3 text-xs rounded-md border border-slate-700 hover:border-indigo-500 disabled:opacity-50"><Upload className="h-3.5 w-3.5" />{busy ? "Uploading…" : "Upload"}</button>
            <input ref={inp} data-testid="doc-file-input" type="file" multiple accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={upload} />
          </div>
        )}
      </div>
      {!docs.length ? <div className="text-sm text-slate-600" data-testid="documents-empty">No files attached.</div> : (
        <ul className="divide-y divide-slate-800/70">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`doc-row-${d.id}`}>
              <FileText className="h-4 w-4 text-indigo-300 shrink-0" />
              <div className="flex-1 min-w-0"><div className="truncate">{d.original_filename}</div><div className="text-xs text-slate-500">{DOC_KINDS[d.kind] || d.kind} · {(d.size / 1024).toFixed(0)} KB · {d.uploader_name} · {fmtDate(d.created_at)}</div></div>
              <a data-testid={`doc-open-${d.id}`} href={authedUrl(`/documents/${d.id}/download`)} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white"><ExternalLink className="h-4 w-4" /></a>
              {canEdit && <button data-testid={`doc-delete-${d.id}`} onClick={() => remove(d)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
